// src/pages/Region/ViewRegions.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import regionService from '../../services/region.service';
import '../../styles/ViewPages.css';

const ViewRegions = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [regions, setRegions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRegions();
  }, [currentPage, searchTerm]);

  const fetchRegions = async () => {
    setLoading(true);
    try {
      const companyId = isSuperAdmin 
        ? localStorage.getItem('selectedCompanyId')
        : user?.companyId;

      if (!companyId) {
        alert('Please select a company first');
        setLoading(false);
        return;
      }

      const response = await regionService.getRegions(
        companyId,
        currentPage,
        10,
        searchTerm
      );

      if (response.success && response.data) {
        setRegions(response.data.regions || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      alert('Failed to fetch regions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        const response = await regionService.deleteRegion(id);
        if (response.success) {
          alert('Region deleted successfully!');
          fetchRegions();
        }
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete region');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="view-regions-container">
      {/* Header */}
      <div className="view-header">
        <h4 className="view-title">View Regions</h4>
        <div className="view-actions">
          <button
            className="btn-add"
            onClick={() => navigate('/region/add')}
          >
            + Add Region
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search regions by company or region name... 🔍"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading regions...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>COMPANY</th>
                <th>REGION</th>
                <th>ADDED DATE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {regions.length > 0 ? (
                regions.map((region) => (
                  <tr key={region.id}>
                    <td>{region.company?.name || 'N/A'}</td>
                    <td>{region.name}</td>
                    <td>{formatDate(region.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/region/view/${region.id}`)}
                          title="View Details"
                        >
                          view
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() => navigate(`/region/edit/${region.id}`)}
                          title="Edit Region"
                        >
                          edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(region.id, region.name)}
                          title="Delete Region"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">
                    {searchTerm ? 'No regions found matching your search' : 'No regions available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            « Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next »
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewRegions;